import React, {useMemo, useCallback} from 'react';
import {
  availJsonSchema,
  availUiSchema,
  dateJsonSchema,
  dateUiSchema,
  rulesJsonSchema,
  rulesJsonSchemaForDays,
  rulesUiSchema,
  rulesUiSchemaForDays
} from '@/containers/ReviewBulkPointModifiers/ReviewBulkPointJSON';
import {Link} from '@/components/atoms';
import ReadOnlyFieldWithJson from '@/components/atoms/ReadOnlyFieldWithJson/ReadOnlyFieldWithJson';
import {PageContainer} from '@/components/organisms';
import {SectionTitle} from '@/components/molecules';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useAppDispatch, useAppSelector} from '@/store';
import {setBulkModifiers} from '@/store/slices/bulkPointSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {formatDateToMMDDYYYY, parseReviewScheduleDate} from '@/utils/commons';
import {
  FILE_UPLOAD_TEMPLATE_TYPE,
  STORE_SELECTOR_DROPDOWN
} from '@/utils/constants/commonConstants';
import {getSasToken} from '@/services/sasToken';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {downloadCSVFiles} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';

const isBlobUrl = (value) =>
  typeof value === 'string' &&
  (value.startsWith('http://') || value.startsWith('https://'));

const ReviewBulkPoint = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {downloadFileOnBlob} = useAzureBlob();

  const bulkModifier = useAppSelector((state) => state?.bulkModifiers);
  const {
    actions = '',
    schedule = '',
    storeSelector = '',
    bulkModifierName = '',
    file = '',
    name = '',
    numberOfPoints = '',
    numberOfDays = '',
    targetMediaFile = [],
    storeList = '',
    scheduledAt = '',
    startDate = ''
  } = bulkModifier;

  const isExcelUpload =
    storeSelector?.toLowerCase() === STORE_SELECTOR_DROPDOWN.EXCEL_UPLOAD;

  const displayFileName = useMemo(() => {
    if (targetMediaFile?.[0]) {
      return targetMediaFile[0];
    }
    if (file && !isBlobUrl(file)) {
      return file;
    }
    if (isBlobUrl(file)) {
      return file.split('/').pop();
    }
    return '';
  }, [targetMediaFile, file]);

  const blobUrl = useMemo(() => {
    if (storeList && isBlobUrl(storeList)) {
      return storeList;
    }
    if (isBlobUrl(file)) {
      return file;
    }
    return '';
  }, [storeList, file]);

  const isPublished =
    bulkModifier?.status?.toString()?.toLowerCase() === 'published';

  const dateToDisplay = useMemo(() => {
    if (isPublished === true) {
      return scheduledAt;
    } else {
      return startDate ?? scheduledAt;
    }
  }, [scheduledAt, startDate, isPublished]);

  const scheduleDateObj = useMemo(
    () => parseReviewScheduleDate(dateToDisplay),
    [dateToDisplay]
  );
  const isScheduleDateInPast = useMemo(() => {
    if (!scheduleDateObj) return false;
    const today = new Date();
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const scheduleMidnight = new Date(
      scheduleDateObj.getFullYear(),
      scheduleDateObj.getMonth(),
      scheduleDateObj.getDate()
    );
    return scheduleMidnight.getTime() < todayMidnight.getTime();
  }, [scheduleDateObj]);

  const handleDownloadTemplate = useCallback(async () => {
    if (!blobUrl) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Template file is not available for download.',
          type: 'error'
        })
      );
      return;
    }

    try {
      const blobFileName = blobUrl.split('/').pop();
      const directory = blobUrl.split('/input')[1];

      const sasResponse = await getSasToken(
        blobFileName,
        FILE_UPLOAD_TEMPLATE_TYPE.BULK_POINT_MODIFIER
      );
      const response = await downloadFileOnBlob(
        sasResponse,
        directory,
        blobFileName
      );

      if (!response?.responseText) {
        throw new Error('No response received from download');
      }

      downloadCSVFiles(response.responseText, displayFileName || blobFileName);
    } catch (error) {
      logger.error('Error downloading bulk modifier template:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Template file is not available for download.',
          type: 'error'
        })
      );
    }
  }, [blobUrl, displayFileName, dispatch, downloadFileOnBlob]);

  const handleEditSection = useCallback(() => {
    const parsedStartDate = parseReviewScheduleDate(bulkModifier?.startDate);
    const normalizedStartDate = parsedStartDate
      ? formatDateToMMDDYYYY(parsedStartDate)
      : bulkModifier?.startDate;

    const resolvedBlobUrl =
      bulkModifier?.storeList ||
      (isBlobUrl(bulkModifier?.file) ? bulkModifier?.file : '');
    const resolvedDisplayName =
      bulkModifier?.targetMediaFile?.[0] ||
      (bulkModifier?.file && !isBlobUrl(bulkModifier?.file)
        ? bulkModifier?.file
        : bulkModifier?.file?.split('/')?.pop()) ||
      '';

    const updatedBulkModifier = {
      ...bulkModifier,
      startDate: normalizedStartDate,
      targetMediaFile: resolvedDisplayName ? [resolvedDisplayName] : [],
      storeList: resolvedBlobUrl,
      numberOfPoints: bulkModifier?.numberOfPoints?.toString(),
      numberOfDays: bulkModifier?.numberOfDays?.toString(),
      ...(isScheduleDateInPast
        ? {
            schedule: '',
            startDate: '',
            endDate: ''
          }
        : {})
    };
    dispatch(setBulkModifiers(updatedBulkModifier));
    navigate(ROUTES.BULK_POINT_MODIFIER_CREATE);
  }, [bulkModifier, dispatch, navigate, isScheduleDateInPast]);

  const RenderSection = useCallback(
    ({id, label, jsonSchema, uiSchema, data}) => (
      <div>
        <SectionTitle isBorderVisible={true} id={id} label={label} />
        <div className='pt-5'>
          <JsonFormsWrapper
            jsonschema={jsonSchema}
            uischema={uiSchema}
            data={data}
          />
        </div>
      </div>
    ),
    []
  );

  return (
    <PageContainer
      data-testid='review-bulk-point-modifiers-content'
      className='review-bulk-modifiers'>
      {(!bulkModifier?.status ||
        bulkModifier?.status?.toLowerCase() === 'draft') && (
        <div className='relative'>
          <div data-testid='targetSetupEdit'>
            <span className='absolute right-0'>
              <Link
                label='Edit Section'
                iconLeading='edit'
                onClick={handleEditSection}
              />
            </span>
          </div>
        </div>
      )}
      <div className='mt-[29px] text-base font-bold'>Bulk Modifier Setup </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={dateJsonSchema}
        uiSchema={dateUiSchema}
        data={{
          eventName: bulkModifierName || name,
          storeSelector: storeSelector
        }}
      />
      <div className='mt-[29px] text-base font-bold'>Rules </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={
          actions === 'Remove All Except'
            ? rulesJsonSchemaForDays
            : rulesJsonSchema
        }
        uiSchema={
          actions === 'Remove All Except' ? rulesUiSchemaForDays : rulesUiSchema
        }
        data={{
          action: actions,
          numberOfPoints: numberOfPoints,
          numberOfDays: numberOfDays
        }}
      />
      <div className='mt-[29px] text-base font-bold'>Availability </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={availJsonSchema}
        uiSchema={availUiSchema(schedule, dateToDisplay)}
        data={{
          schedule: schedule,
          date: dateToDisplay
        }}
      />
      {!isPublished && isScheduleDateInPast && (
        <div className='mt-2 text-sm text-[#d20000]'>
          Note: The scheduled date cannot be in the past. Please reselect
          'Schedule' on the setup page.
        </div>
      )}
      {isExcelUpload && (
        <>
          <div className='mt-[29px] text-base font-bold'>File </div>
          <div>
            <SectionTitle isBorderVisible={true} id='review-file' label='' />
            <div
              className='review-bulk-filters review_file_color pt-5'
              data-testid='review-bulk-template-file'>
              <ReadOnlyFieldWithJson
                label='Template'
                data={
                  displayFileName ? (
                    <Link
                      className='text-sky-500'
                      onClick={handleDownloadTemplate}>
                      <span>{displayFileName}</span>
                    </Link>
                  ) : (
                    '-'
                  )
                }
              />
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default React.memo(ReviewBulkPoint);
